import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Button,
  List,
  Card,
  Spin,
  Empty,
  Alert,
  Layout,
  Avatar,
  Row,
  Col,
  Select,
  message,
  Space,
} from 'antd';
import { DownloadOutlined, FileTextOutlined, ShareAltOutlined } from '@ant-design/icons';
import { customFetch } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const { Content } = Layout;
const { Title, Paragraph } = Typography;
const { Option } = Select;

interface PublicFileType {
  id: string;
  file: string; // This is the S3 path
  s3_path: string; // This is the user-friendly S3 path with bucket name
  download_url: string;
  file_name: string;
  size: number;
  product_name?: string;
  product_version?: string;
  uploaded_at: string;
  description?: string;
}

interface ProductType {
  id: string;
  name: string;
}

function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export const DownloadPage: React.FC = () => {
  const [products, setProducts] = useState<ProductType[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [files, setFiles] = useState<PublicFileType[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchProducts = useCallback(async () => {
    try {
      const response = await customFetch('/api/s3/public/products/');
      if (!response.ok) {
        throw new Error('Failed to fetch products. Please try again later.');
      }
      const data = await response.json();
      const productList = data.results || data;
      setProducts(productList);

      // If there are products and none is selected, select the first one by default.
      if (productList.length > 0 && !selectedProduct) {
        setSelectedProduct(productList[0].id);
      }

    } catch (err: any) {
      setError(err.message);
    }
  }, [selectedProduct]); // Add selectedProduct to dependency array

  const fetchFiles = useCallback(async () => {
    if (!selectedProduct) {
      setFiles([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await customFetch(`/api/s3/public/files/?product_id=${selectedProduct}`);
      if (!response.ok) {
        throw new Error('Failed to fetch files for the selected product.');
      }
      const data = await response.json();
      setFiles(data.results || data);
    } catch (err: any) {
      setError(err.message);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProduct]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleShare = (file: PublicFileType) => {
    const shareUrl = `${window.location.origin}/download/${file.id}`;
    navigator.clipboard.writeText(shareUrl).then(
      () => {
        message.success('Shareable link copied to clipboard!');
      },
      (err) => {
        message.error('Failed to copy link.');
        console.error('Could not copy link: ', err);
      }
    );
  };

  const handleDownload = async (file: PublicFileType) => {
    setDownloading({ ...downloading, [file.id]: true });
    
    try {
      const token = localStorage.getItem('token');
      const headers = new Headers();
      if (token) {
        headers.append('Authorization', `Token ${token}`);
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'}${file.download_url}`, {
        method: 'GET',
        headers: headers,
      });

      if (response.status === 401) {
        message.error("You must be logged in to download this file. Redirecting to login...");
        window.location.href = '/login'; 
        return;
      }
      
      if (response.status === 403) {
        message.error("You do not have permission to download this file.");
        return;
      }

      if (!response.ok) {
        throw new Error('An unexpected error occurred during download.');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', file.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

    } catch (err: any) {
      message.error('An unexpected error occurred during download.');
    } finally {
      setDownloading({ ...downloading, [file.id]: false });
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Content style={{ width: '100%', margin: '24px auto', padding: '0 24px' }}>
        <div style={{ background: '#fff', padding: '24px', borderRadius: '8px' }}>
          <Title level={2} style={{ marginBottom: '24px' }}>Downloads</Title>
          
          <Select
            showSearch
            placeholder="Select a product to see available files"
            style={{ width: '100%', maxWidth: '400px', marginBottom: '24px' }}
            onChange={(value) => setSelectedProduct(value)}
            value={selectedProduct}
            filterOption={(input, option) =>
              (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {products.map(product => (
              <Option key={product.id} value={product.id}>{product.name}</Option>
            ))}
          </Select>

          <Card bordered={false}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>
            ) : error ? (
              <Alert message={error} type="error" showIcon />
            ) : (
              <List
                dataSource={files}
                renderItem={(item, index) => (
                  <List.Item>
                    <Row align="middle" gutter={[16, 16]} style={{ width: '100%' }}>
                      <Col flex="48px">
                        <Avatar shape="square" size={48} icon={<FileTextOutlined />} />
                      </Col>
                      <Col flex="auto">
                        <div style={{ fontWeight: 500, fontSize: '18px' }}>{`${index + 1}. ${item.product_name || item.file_name} ${item.product_version ? `(v${item.product_version})` : ''}`}</div>
                        {item.description && <Paragraph type="secondary" style={{ margin: '4px 0' }}>{item.description}</Paragraph>}
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          <div><strong>File Name:</strong> {item.file_name}</div>
                          {user?.is_staff && <div><strong>S3 Path:</strong> {item.s3_path}</div>}
                          <div><strong>Size:</strong> {formatBytes(item.size)} | <strong>Released:</strong> {new Date(item.uploaded_at).toLocaleString()}</div>
                        </div>
                      </Col>
                      <Col>
                        <Space>
                            {user?.is_staff && (
                                <Button icon={<ShareAltOutlined />} onClick={() => handleShare(item)}>
                                    Share
                                </Button>
                            )}
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                loading={downloading[item.id]}
                                onClick={() => handleDownload(item)}
                            >
                                Download
                            </Button>
                        </Space>
                      </Col>
                    </Row>
                  </List.Item>
                )}
                locale={{ emptyText: <Empty description={selectedProduct ? "No releases found for this product." : "Please select a product to view files."} /> }}
              />
            )}
          </Card>
        </div>
      </Content>
    </Layout>
  );
}; 